import { useGetList } from 'react-admin';
import { useState } from 'react';
import { useMediaQuery, Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, Avatar, TablePagination } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Warning } from '@mui/icons-material';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { SEVERITY_COLORS } from './constants';

interface IncidentItem {
  id: string;
  status: string;
  severity?: string;
  employee?: { id: string };
  injuryNumber?: string;
  nature?: string;
  incidentDate?: string;
  reportedToDol?: boolean;
}

export const IncidentList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: incidents, isLoading } = useGetList('workplace-injuries', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();

  const stats = {
    total: incidents?.length ?? 0,
    open: incidents?.filter((i: IncidentItem) => i.status === 'Reported').length ?? 0,
    investigating: incidents?.filter((i: IncidentItem) => i.status === 'Investigating').length ?? 0,
    resolved: incidents?.filter((i: IncidentItem) => i.status === 'Closed' || i.status === 'Disputed').length ?? 0,
    dolRequired: incidents?.filter((i: IncidentItem) => i.severity === 'Major' || i.severity === 'Fatal').length ?? 0,
  };

  const severityCounts: Record<string, number> = incidents?.reduce((acc: Record<string, number>, i: IncidentItem) => {
    acc[i.severity ?? 'Unknown'] = (acc[i.severity ?? 'Unknown'] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(severityCounts).map(([label, value]) => ({ label, value }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.ohs" wizardPath="wizard/injury" label="Report Injury" />
        </Box>
        <ListPageHeader
          title="OHS Incidents"
          metrics={[
            { label: 'Open', value: String(stats.open), color: 'error.main' },
            { label: 'Investigating', value: String(stats.investigating), color: 'warning.main' },
            { label: 'Resolved', value: String(stats.resolved), color: 'success.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {incidents?.map((incident: IncidentItem) => (
            <Paper key={incident.id} sx={{ p: 2 }} onClick={() => navigate(`/workplace-injuries/${incident.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: SEVERITY_COLORS[incident.severity ?? '']?.replace('.main', '') || 'grey.500', fontSize: 14 }}>
                    <Warning sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, incident.employee?.id)}</Typography>
                    <Typography variant="caption" color="text.secondary">{incident.injuryNumber}</Typography>
                  </Box>
                </Box>
                  <Chip label={incident.severity} size="small" sx={{ bgcolor: SEVERITY_COLORS[incident.severity ?? ''], color: 'white' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{incident.nature}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{incident.incidentDate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip label={incident.status} size="small" color={incident.status === 'Reported' ? 'error' : incident.status === 'Closed' ? 'success' : 'warning'} />
                {incident.reportedToDol ? (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>DOL Reported</Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>DOL Pending</Typography>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.ohs" wizardPath="wizard/injury" label="Report Injury" />
      </Box>
      <ListPageHeader
        title="OHS Incidents"
        metrics={[
          { label: 'Open', value: String(stats.open), color: 'error.main' },
          { label: 'Investigating', value: String(stats.investigating), color: 'warning.main' },
          { label: 'Resolved', value: String(stats.resolved), color: 'success.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Severity"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>DOL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {incidents?.map((incident: IncidentItem) => (
              <TableRow
                key={incident.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/workplace-injuries/${incident.id}/show`)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{incident.injuryNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: SEVERITY_COLORS[incident.severity ?? '']?.replace('.main', '') || 'grey.500', fontSize: 12 }}>
                      <Warning sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, incident.employee?.id)}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{incident.nature}</Typography>
                </TableCell>
                <TableCell>
                <Chip label={incident.severity} size="small" sx={{ bgcolor: SEVERITY_COLORS[incident.severity ?? ''], color: 'white' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{incident.incidentDate}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={incident.status}
                    size="small"
                    color={incident.status === 'Reported' ? 'error' : incident.status === 'Closed' || incident.status === 'Disputed' ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell>
                  {incident.reportedToDol ? (
                    <Chip label="Yes" size="small" color="success" />
                  ) : (
                    <Chip label="No" size="small" color="warning" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={incidents?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!incidents || incidents.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Warning sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No incidents found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
